import { Box, Card, Skeleton, Typography } from '@mui/material';
import { Fragment } from 'react';

import { AvDenovoPreviewModal } from '@verantos-dev/feature-analytical-variables';
import {
  SortableCriteriaItem,
  SortableCriteriaList,
  type SortableCriteriaItemDragHandleProps,
} from '@verantos-dev/feature-cohort-builder';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AddVariablePickerModal } from './AddVariablePickerModal';
import { AnalyticalVariableCard } from '../../components/AnalyticalVariableCard';
import { SectionHeader } from '../../components/SectionHeader';
import { SubtleSurface } from '../../components/SubtleSurface';
import { useAdRouteParams } from '../../hooks/useAdRouteParams';
import { useAnalyticalVariablesSection } from '../../hooks/useAnalyticalVariablesSection';
import { useAnalyticalDatasetDefinitionStore } from '../../state/useAnalyticalDatasetDefinitionStore';
import type { AnalyticalVariablesSectionProps } from '../../types';

// Mirror the cohort-builder Criteria card grid so analytical-variable cards keep
// a fixed minimum width and wrap (instead of stretching full-width), matching the
// approved design's cross-artifact consistency.
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 2,
  minWidth: 0,
} as const;

export const AnalyticalVariablesSection = ({
  isLoading = false,
}: AnalyticalVariablesSectionProps) => {
  const { projectId } = useAdRouteParams();
  const {
    sectionLabel,
    count,
    addLabel,
    emptyLabel,
    rows,
    isPickerOpen,
    isLoading: isSectionLoading,
    onAddVariable,
    onClosePicker,
    onRemoveVariable,
    previewAvId,
    onViewVariable,
    onClosePreview,
    isEditDisabled,
    editLockTooltip,
  } = useAnalyticalVariablesSection();
  const reorderVariables = useAnalyticalDatasetDefinitionStore((s) => s.reorderVariables);
  const isDragDisabled = isEditDisabled;
  const draggableAvIds = rows
    .filter((row) => !row.isDefault)
    .map((row) => row.analyticalVariableId);

  // Parent covers "store hasn't hydrated yet"; section covers "store has
  // user-variables but the AV library lookup hasn't resolved yet".
  const showSkeleton = isLoading || isSectionLoading;

  const renderVariablesGrid = () => {
    const grid = (
      <Box sx={GRID_SX}>
        {rows.map((row) => {
          const card = (dragHandleProps?: SortableCriteriaItemDragHandleProps) => (
            <AnalyticalVariableCard
              id={row.analyticalVariableId}
              serialNumber={row.serialNumber}
              name={row.name}
              description={row.description}
              format={row.format}
              isDefault={row.isDefault}
              onDelete={
                row.isDefault ? undefined : () => onRemoveVariable(row.analyticalVariableId)
              }
              disableDelete={isEditDisabled}
              deleteTooltip={editLockTooltip}
              // Default rows are synthetic (no backing AV), so they
              // aren't clickable — only real variables open the
              // detail preview.
              onClick={row.isDefault ? undefined : () => onViewVariable(row.analyticalVariableId)}
              dragHandleProps={dragHandleProps}
            />
          );

          return isDragDisabled || row.isDefault ? (
            <Fragment key={row.analyticalVariableId}>{card()}</Fragment>
          ) : (
            <SortableCriteriaItem key={row.analyticalVariableId} id={row.analyticalVariableId}>
              {(dragHandleProps) => card(dragHandleProps)}
            </SortableCriteriaItem>
          );
        })}
      </Box>
    );

    return isDragDisabled ? (
      grid
    ) : (
      <SortableCriteriaList itemIds={draggableAvIds} onReorder={reorderVariables}>
        {grid}
      </SortableCriteriaList>
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          // Figma: section card padding = spacing-6 (24px).
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          // Figma: 24px title-to-content gap.
          gap: 6,
          minWidth: 0,
        }}
      >
        <SectionHeader
          label={sectionLabel}
          count={count}
          action={{
            label: addLabel,
            onClick: onAddVariable,
            disabled: isEditDisabled,
            tooltip: editLockTooltip,
            dataTestId: 'ad-variables-add',
          }}
        />
        {showSkeleton ? (
          <Box sx={GRID_SX} data-testid="ad-variables-loading">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rounded"
                height={96}
                animation="wave"
                sx={{ borderRadius: `${borderRadius.md}px` }}
              />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <SubtleSurface
            sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}
            data-testid="ad-variables-empty"
          >
            <Typography variant="body2">{emptyLabel}</Typography>
          </SubtleSurface>
        ) : (
          renderVariablesGrid()
        )}
      </Card>
      {isPickerOpen && <AddVariablePickerModal projectId={projectId} onClose={onClosePicker} />}
      {/* Reuses the analytical-variables detail surface (SQL tabs etc.).
          Fetches lazily — the query only fires while the modal is open. */}
      <AvDenovoPreviewModal
        id="ad-av-preview"
        analyticalVariableId={previewAvId ?? ''}
        isOpen={Boolean(previewAvId)}
        onClose={onClosePreview}
      />
    </>
  );
};
